import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown-menu"
import { Button } from "../button"
import { MoreVertical } from "lucide-react"

export type TableColumn<T> = {
  key: keyof T
  label: string
  render?: (row: T) => React.ReactNode
  headerClassName?: string
  cellClassName?: string
}

export type Action<T> = {
  label: React.ReactNode
  onClick: (row: T) => void
  className?: string
  disabled?: boolean | ((row: T) => boolean)
  hidden?: (row: T) => boolean
}

type DataTableProps<T> = {
  columns: TableColumn<T>[]
  data: T[]
  actions?: Action<T>[] | ((row: T) => Action<T>[])
  emptyMessage?: React.ReactNode
  onRowClick?: (row: T) => void
}

export function DataTable<T extends { id: string | number }>({
  columns,
  data,
  actions,
  emptyMessage,
  onRowClick,
}: DataTableProps<T>) {
  const getRowActions = (row: T): Action<T>[] => {
    if (!actions) return []
    return typeof actions === "function" ? actions(row) : actions
  }

  const hasActions = actions !== undefined

  return (
    <div className="rounded-xl bg-card border border-border shadow-sm overflow-hidden">
      <div className="w-full">
        <Table className="w-full">
          <TableHeader>
            <TableRow className="border-b border-border">
              {columns.map((col) => (
                <TableHead
                  key={String(col.key)}
                  className={`bg-muted/50 px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider ${col.headerClassName || ""}`}
                >
                  {col.label}
                </TableHead>
              ))}
              {hasActions && (
                <TableHead className="text-right bg-muted/50 px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  className="text-center py-12 text-muted-foreground text-sm"
                >
                  {emptyMessage || "No data available."}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const rowActions = getRowActions(row)

                return (
                  <TableRow
                    key={row.id}
                    className={`border-b border-border hover:bg-muted/30 transition-colors duration-150 ${onRowClick ? "cursor-pointer" : ""}`}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={String(col.key)}
                        className={`px-6 py-4 text-sm text-foreground ${col.cellClassName || ""}`}
                      >
                        {col.render ? col.render(row) : String(row[col.key])}
                      </TableCell>
                    ))}

                    {hasActions && rowActions.length > 0 && (
                      <TableCell className="text-right px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 hover:bg-muted transition-colors cursor-pointer"
                            >
                              <MoreVertical className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent
                            align="end"
                            className="bg-popover shadow-lg border border-border rounded-lg min-w-40"
                          >
                            {rowActions.map((action, index) => {
                              if (action.hidden && action.hidden(row)) {
                                return null
                              }

                              const isDisabled =
                                typeof action.disabled === "function"
                                  ? action.disabled(row)
                                  : action.disabled === true

                              return (
                                <DropdownMenuItem
                                  key={index}
                                  disabled={isDisabled}
                                  onClick={() => {
                                    if (!isDisabled) {
                                      action.onClick(row)
                                    }
                                  }}
                                  className={`
                                    px-4 py-2.5 text-sm transition-colors
                                    ${
                                      isDisabled
                                        ? "text-muted-foreground/50 cursor-not-allowed opacity-60"
                                        : "text-foreground hover:bg-muted/50 cursor-pointer"
                                    }
                                    ${action.className || ""}
                                  `}
                                >
                                  {action.label}
                                </DropdownMenuItem>
                              )
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}

                    {hasActions && rowActions.length === 0 && (
                      <TableCell className="text-right px-6 py-4">
                        <span className="text-xs text-muted-foreground/50">
                          No actions
                        </span>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
